<script lang="ts" setup>
import "@cisco-u/reicon-lock/cu-reicon-lock.js";

import dayjs from "dayjs";
import { debounce } from "lodash-es";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import AnchorTargetBlankCallback from "@/components/AnchorTargetBlankCallback.vue";
import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { addUserHistory } from "@/services/profileService/addUserHistory";
import isUnauth from "@/utils/isUnauth";
import { toggleMyListItem } from "@/utils/myListActions";
import telemetry from "@/utils/telemetry";

const props = defineProps<{
  result: any;
  index?: number;
  status?: any;
  forCarousel?: boolean;
  cardView?: string;
  cardStyle?: string;
  priceView?: string;
  topicView?: boolean;
  isStoreContent?: boolean;
  showDescription?: boolean;
}>();

const router = useRouter();
const route = useRoute();
const store = useStore();

const click = ref(false);

const subLink = computed(() => {
  return router.resolve({
    name: "subscription",
  }).href;
});

const inMyList = computed(() => store.getters["user/inMyList"]);
const contentAuthorized = computed(
  () => store.getters["content/authorizedContent"]
);
const freeId = computed(() => store.getters["content/freeId"]);

const freeContent = computed((): boolean => {
  return props.result?.content_bundle?.includes(freeId.value);
});

const showPremium = computed((): boolean => {
  return !props.status?.userEntitlements?.includes(props.result?.guid);
});

const resultLink = computed(() => {
  const podcastId = props.result?.friendly_url || props.result?.id;

  return podcastId
    ? router.resolve({ name: "PodcastDetails", params: { podcastId } }).href
    : "";
});

const bookmarkMe = (content: any) => {
  toggleMyListItem(content, content.type, {
    inMyList: inMyList.value,
    addContentToMyList: (payload: any) =>
      store.dispatch("user/addContentToMyList", payload),
    removeContentFromMyList: (payload: any) =>
      store.dispatch("user/removeContentFromMyList", payload),
    route,
  });
};

const debouncedBookmarkMe = debounce(
  (content: any) => bookmarkMe(content),
  500
);

const whereTo = () => {
  if (showPremium.value) {
    click.value = true;
  } else {
    click.value = false;
    sendTelemetry();
  }
};

const hideClicked = () => {
  if (click.value && showPremium.value) {
    click.value = false;
  }
};

const sendTelemetry = async () => {
  let telemetryResponse = telemetry.external(
    {
      action: "launch-contentType",
      url: click.value ? subLink.value : props.result.id,
      title: click.value ? "Subscribe to Cisco U." : props.result.title,
      id: props.result.id,
      page: route.fullPath,
      type: click.value ? "subscription offer" : props.result.type,
      relevance: props.result.relevance,
      sortOrder: props.index,
    },
    isUnauth()
  );
  if (isUnauth()) {
    const routeResolved = router.resolve({ name: "subscription" });
    window.open(routeResolved.href, "_blank", "noopener");
    return;
  }
  await addUserHistory({
    content_id: props.result.id,
    type: "podcast",
    accessed: dayjs().toISOString(),
  });
  telemetryResponse
    .then(() => {
      if (showPremium.value) {
        window.open(subLink.value, "_blank", "noopener");
      } else if (props.result.publisher_name == "Cisco") {
        router.push(resultLink.value);
      } else {
        window.open(resultLink.value, "_blank", "noopener");
      }
    })
    .catch(() => {
      // do nothing
    });
};

defineExpose({
  click,
  showPremium,
  freeContent,
  hideClicked,
  addContentToMyList: (payload: any) =>
    store.dispatch("user/addContentToMyList", payload),
  removeContentFromMyList: (payload: any) =>
    store.dispatch("user/removeContentFromMyList", payload),
  debouncedBookmarkMe,
  resultLink,
});
</script>

<template>
  <div
    v-show="result"
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :link="resultLink"
      :cy="'pod-cast-card'"
      :show-description="props.showDescription !== false"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'pod-cast-product-card'"
      :link="resultLink"
      :show-description="props.showDescription !== false"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <div
      :class="[
        'mt-0',
        {
          hidden: !showPremium || !click,
          'mask_it absolute left-0 top-0 mt-0 h-full w-full rounded-[0.625rem] bg-[#000000]/80':
            showPremium && click,
        },
      ]"
      v-if="click"
    >
      <cu-reicon-lock
        type="filled-keyhole"
        dark="true"
        size="xl"
        class="mt-4 flex justify-center"
      />
      <div
        class="mx-12 text-center text-13 text-white-lightest"
        v-click-outside="{
          handler: hideClicked,
          events: ['dblclick', 'click'],
          capture: true,
        }"
      >
        <div v-if="isUnauth() && contentAuthorized(result.guid)">
          <a href="/login" class="text-white-lightest underline">Sign up</a>
        </div>
        <AnchorTargetBlankCallback
          v-else
          :url="subLink"
          class="underline"
          @clicked="sendTelemetry"
        >
          <div>Upgrade your subscription</div>
        </AnchorTargetBlankCallback>
        <div v-if="isUnauth() && contentAuthorized(result.guid)">
          to explore this free podcast.
        </div>
        <div v-else>to unlock this podcast.</div>
      </div>
    </div>
  </div>
</template>
